import { Navigate, RouteObject } from 'react-router-dom';
import { useAuth } from '../stores/authStore';
import { ReactNode } from 'react';

// Pages (lazy loading will be added when pages are implemented)
import { LoginPage } from '../pages/Login/LoginPage';
import { DashboardPage } from '../pages/Dashboard/DashboardPage';

// Layouts
import { AppLayout } from '../layouts/AppLayout';
import { AuthLayout } from '../layouts/AuthLayout';

// ---------------------------------------------------------------------------
// Route guard
// ---------------------------------------------------------------------------

function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: 'var(--color-bg-primary, #0f0f1a)',
          color: 'var(--color-text-primary, #fff)',
          fontFamily: 'var(--font-primary, Inter, sans-serif)',
        }}
      >
        Loading…
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function RequireGuest({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

import { usePermissions, Permissions, PermissionCode } from '../features/admin/hooks/usePermissions';

function RequireAdmin({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { isTenantAdmin, isPlatformAdmin } = usePermissions();

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: 'var(--color-bg-primary, #0f0f1a)',
          color: 'var(--color-text-primary, #fff)',
          fontFamily: 'var(--font-primary, Inter, sans-serif)',
        }}
      >
        Loading…
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isTenantAdmin && !isPlatformAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }
  return <>{children}</>;
}

function RequirePlatformAdmin({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { isPlatformAdmin } = usePermissions();

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: 'var(--color-bg-primary, #0f0f1a)',
          color: 'var(--color-text-primary, #fff)',
          fontFamily: 'var(--font-primary, Inter, sans-serif)',
        }}
      >
        Loading…
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isPlatformAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }
  return <>{children}</>;
}

function RequirePermission({ permission, children }: { permission: PermissionCode; children: ReactNode }) {
  const { can, isPlatformAdmin, isTenantAdmin } = usePermissions();
  if (!isPlatformAdmin && !isTenantAdmin && !can(permission)) {
    return <Navigate to="/unauthorized" replace />;
  }
  return <>{children}</>;
}

// Admin Pages
import { AdminDashboardPage } from '../features/admin/pages/AdminDashboardPage';
import { TenantListPage } from '../features/admin/pages/TenantListPage';
import { TenantDetailsPage } from '../features/admin/pages/TenantDetailsPage';
import { TenantUsersPage } from '../features/admin/pages/TenantUsersPage';
import { TenantLLMSettingsPage } from '../features/admin/pages/TenantLLMSettingsPage';
import { AdminAnalyticsPage } from '../features/admin/pages/AdminAnalyticsPage';
import { EvaluationStudioPage } from '../features/admin/pages/EvaluationStudioPage';
import { AuditLogPage } from '../features/admin/pages/AuditLogPage';
import { LlmAuditTrailPage } from '../features/admin/pages/LlmAuditTrailPage';
import { AdminReportsPage } from '../features/admin/pages/AdminReportsPage';
import { TenantNotificationSettingsPage } from '../features/admin/pages/TenantNotificationSettingsPage';
import { TenantUDFSettingsPage } from '../features/admin/pages/TenantUDFSettingsPage';
import { PackageManagementPage } from '../features/admin/pages/PackageManagementPage';
import { TenantBillingPage } from '../features/admin/pages/TenantBillingPage';
import { TenantStorageSettingsPage } from '../features/admin/pages/TenantStorageSettingsPage';

// Scenario Studio Pages (Phase 4 & Phase 9 Briefing)
import { ScenarioListPage } from '../features/scenarios/pages/ScenarioListPage';
import { ScenarioCreatePage } from '../features/scenarios/pages/ScenarioCreatePage';
import { ScenarioDetailsPage } from '../features/scenarios/pages/ScenarioDetailsPage';
import { ScenarioEditPage } from '../features/scenarios/pages/ScenarioEditPage';
import { ScenarioBriefingPage } from '../features/scenarios/pages/ScenarioBriefingPage';

// Phase 7: Attempts, History & Resume
import { MyAttemptsPage, AttemptDetailsPage, AttemptHistoryPage } from '../features/attempts';

// Phase 8: Evaluation & Results
import { ResultsPage } from '../features/evaluation';

// Simulation Room (Phase 6) & Dedicated PWA Simulation Room
import { PwaSimulationRoom, SimulationDispatcher } from '../features/simulation';

// Status / Fallback Pages
import { NotFoundPage } from '../pages/NotFound/NotFoundPage';
import { UnauthorizedPage } from '../pages/Unauthorized/UnauthorizedPage';

// ---------------------------------------------------------------------------
// Route definitions
// ---------------------------------------------------------------------------

export const routes: RouteObject[] = [
  // Auth routes
  {
    element: (
      <RequireGuest>
        <AuthLayout />
      </RequireGuest>
    ),
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/login/:tenantSlug', element: <LoginPage /> },
      { path: '/org/:tenantSlug/login', element: <LoginPage /> },
    ],
  },

  // Protected app routes
  {
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: '/dashboard', element: <DashboardPage /> },

      // Phase 3 & 9: Admin routes
      { path: '/admin', element: <RequireAdmin><AdminDashboardPage /></RequireAdmin> },
      { path: '/admin/tenants', element: <RequirePlatformAdmin><TenantListPage /></RequirePlatformAdmin> },
      { path: '/admin/tenants/:id', element: <RequireAdmin><TenantDetailsPage /></RequireAdmin> },
      { path: '/admin/users', element: <RequireAdmin><TenantUsersPage /></RequireAdmin> },
      { path: '/admin/llm-config', element: <RequireAdmin><TenantLLMSettingsPage /></RequireAdmin> },
      { path: '/admin/analytics', element: <RequireAdmin><AdminAnalyticsPage /></RequireAdmin> },
      { path: '/admin/reports', element: <RequirePermission permission={Permissions.ANALYTICS_EXPORT}><AdminReportsPage /></RequirePermission> },
      { path: '/reports', element: <RequirePermission permission={Permissions.ANALYTICS_EXPORT}><AdminReportsPage /></RequirePermission> },
      { path: '/admin/settings', element: <RequireAdmin><TenantNotificationSettingsPage /></RequireAdmin> },
      { path: '/admin/settings/notifications', element: <RequireAdmin><TenantNotificationSettingsPage /></RequireAdmin> },
      { path: '/admin/settings/storage', element: <RequireAdmin><TenantStorageSettingsPage /></RequireAdmin> },
      { path: '/admin/storage-config', element: <RequireAdmin><TenantStorageSettingsPage /></RequireAdmin> },
      { path: '/admin/storage', element: <RequireAdmin><TenantStorageSettingsPage /></RequireAdmin> },
      { path: '/admin/settings/udf', element: <RequireAdmin><TenantUDFSettingsPage /></RequireAdmin> },
      { path: '/admin/custom-fields', element: <RequireAdmin><TenantUDFSettingsPage /></RequireAdmin> },
      { path: '/admin/udf', element: <RequireAdmin><TenantUDFSettingsPage /></RequireAdmin> },
      { path: '/admin/notifications', element: <RequireAdmin><TenantNotificationSettingsPage /></RequireAdmin> },
      { path: '/admin/evaluations', element: <RequireAdmin><EvaluationStudioPage /></RequireAdmin> },
      { path: '/admin/audit-logs', element: <RequireAdmin><AuditLogPage /></RequireAdmin> },
      { path: '/admin/llm-audit', element: <RequireAdmin><LlmAuditTrailPage /></RequireAdmin> },
      { path: '/admin/llm-logs', element: <RequireAdmin><LlmAuditTrailPage /></RequireAdmin> },
      { path: '/admin/llm-audit-trail', element: <RequireAdmin><LlmAuditTrailPage /></RequireAdmin> },
      { path: '/admin/llm-execution-audit', element: <RequireAdmin><LlmAuditTrailPage /></RequireAdmin> },
      { path: '/admin/token-audit', element: <RequireAdmin><LlmAuditTrailPage /></RequireAdmin> },
      { path: '/admin/llm-execution', element: <RequireAdmin><LlmAuditTrailPage /></RequireAdmin> },
      { path: '/llm-audit', element: <RequireAdmin><LlmAuditTrailPage /></RequireAdmin> },
      { path: '/llm-audit-trail', element: <RequireAdmin><LlmAuditTrailPage /></RequireAdmin> },

      // Enterprise Monetization & Packages
      { path: '/admin/packages', element: <RequirePlatformAdmin><PackageManagementPage /></RequirePlatformAdmin> },
      { path: '/admin/billing', element: <RequireAdmin><TenantBillingPage /></RequireAdmin> },
      { path: '/billing', element: <RequireAdmin><TenantBillingPage /></RequireAdmin> },

      // Phase 4, 9 & 27: Scenario / Case Studio routes, Evaluation Studio & Briefing
      { path: '/scenarios', element: <RequirePermission permission={Permissions.SCENARIO_READ}><ScenarioListPage /></RequirePermission> },
      { path: '/scenarios/new', element: <RequirePermission permission={Permissions.SCENARIO_CREATE_EDIT}><ScenarioCreatePage /></RequirePermission> },
      { path: '/scenarios/:id', element: <RequirePermission permission={Permissions.SCENARIO_READ}><ScenarioDetailsPage /></RequirePermission> },
      { path: '/scenarios/:id/edit', element: <RequirePermission permission={Permissions.SCENARIO_CREATE_EDIT}><ScenarioEditPage /></RequirePermission> },
      { path: '/scenarios/:scenarioId/evaluation-config', element: <RequirePermission permission={Permissions.SCENARIO_CREATE_EDIT}><EvaluationStudioPage /></RequirePermission> },
      { path: '/scenarios/:scenarioId/evaluation-studio', element: <RequirePermission permission={Permissions.SCENARIO_CREATE_EDIT}><EvaluationStudioPage /></RequirePermission> },
      { path: '/simulations/:id/briefing', element: <ScenarioBriefingPage /> },

      // Phase 7: Attempts & History routes
      { path: '/attempts', element: <MyAttemptsPage /> },
      { path: '/attempts/history', element: <AttemptHistoryPage /> },
      { path: '/attempts/:id', element: <AttemptDetailsPage /> },

      // Phase 8: Evaluation & Results routes
      { path: '/attempts/:id/results', element: <ResultsPage /> },
      { path: '/results/:id', element: <ResultsPage /> },

      // Security / Status Pages
      { path: '/unauthorized', element: <UnauthorizedPage /> },
      { path: '/404', element: <NotFoundPage /> },
    ],
  },

  // Phase 6: Dedicated PWA Simulation Room (Cloned & isolated for mobile PWA standalone)
  {
    path: '/simulations/:scenarioId/pwa-run',
    element: (
      <RequireAuth>
        <PwaSimulationRoom />
      </RequireAuth>
    ),
  },

  // Phase 6: Live Simulation Room (with smart dispatcher resolving PWA vs Desktop Website)
  {
    path: '/simulations/:scenarioId/run',
    element: (
      <RequireAuth>
        <SimulationDispatcher />
      </RequireAuth>
    ),
  },

  // Catch-all
  { path: '*', element: <Navigate to="/dashboard" replace /> },
];

